import React, { useState } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ArrowLeft, ArrowRight } from 'lucide-react';

export default function EmergentEmpathy() {
  const [currentStep, setCurrentStep] = useState(0);
  const [state, setState] = useState({ blue: '', red: '', green: '', gold: '' });

  const feelings = ['hurt', 'angry', 'sad', 'scared', 'overwhelmed', 'frustrated', 'confused', 'lonely', 'disappointed', 'embarrassed'];

  const steps = [
    { id: 'landing', title: 'First, slow down.', subtitle: "You're in a hard moment. This helps you speak from your heart, not from adrenaline.", color: 'from-slate-800 to-slate-900' },
    { id: 'blue', title: 'Blue · Observation', subtitle: 'What happened? Imagine a camera recording. No blame, no adjectives.', color: 'from-blue-900/80 to-slate-900', ring: 'ring-blue-500/60' },
    { id: 'red', title: 'Red · Feelings', subtitle: 'How do you feel right now?', color: 'from-red-900/80 to-slate-900', ring: 'ring-red-500/60' },
    { id: 'green', title: 'Green · Needs/Values', subtitle: 'What needs or values are alive in you?', color: 'from-emerald-900/80 to-slate-900', ring: 'ring-emerald-500/60' },
    { id: 'gold', title: 'Gold · Request', subtitle: 'What is a simple, doable request for right now?', color: 'from-amber-900/80 to-slate-900', ring: 'ring-amber-400/70' },
    { id: 'summary', title: 'Your Gold Sentence', subtitle: 'Read this aloud, or tweak it first. Then pass the phone.', color: 'from-slate-900 to-emerald-900/60' }
  ];

  const buildGoldSentence = () => {
    return `When ${state.blue || '[describe what happened]'}, I feel ${state.red || '[your feelings]'} because I need ${state.green || '[what you need or value]'}. Would you be willing to ${state.gold || '[make a simple, doable request]'}?`;
  };

  const addFeeling = (feeling) => {
    if (!state.red.toLowerCase().includes(feeling)) {
      setState(prev => ({ ...prev, red: prev.red ? `${prev.red}, ${feeling}` : feeling }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-slate-900/80 backdrop-blur border border-slate-700/80 rounded-2xl shadow-2xl p-6">
          <header className="mb-4">
            <p className="text-xs uppercase tracking-widest text-slate-400 text-center">Belonging Sessions</p>
            <h1 className="mt-2 text-2xl font-semibold text-center text-white">Emergent Empathy</h1>
            <p className="mt-1 text-sm text-center text-slate-400">A quick, color-coded guide for hard moments.</p>
          </header>

          {currentStep > 0 && (
            <div className="flex justify-center gap-2 mb-4">
              {steps.slice(1, 6).map((_, idx) => (
                <div key={idx} className={`h-2 w-2 rounded-full transition ${idx + 1 === currentStep ? 'bg-emerald-400' : 'bg-slate-600/60'}`}></div>
              ))}
            </div>
          )}

          <div className="space-y-3">
            {currentStep === 0 && (
              <div className={`rounded-2xl bg-gradient-to-br ${steps[0].color} ring-1 ring-slate-700/80 p-5`}>
                <p className="text-xs font-medium tracking-wide text-slate-300 mb-3 text-center">In case of emotional emergency:</p>
                <p className="text-sm text-slate-200 mb-3 text-center">
                  This app helps you say what's in your heart using the <span className="font-semibold text-emerald-300">Blue · Red · Green · Gold</span> Belonging Sessions flow.
                </p>
                <ol className="list-decimal list-inside text-xs text-slate-300 space-y-1 mb-3">
                  <li>Pause. Feel your feet on the ground.</li>
                  <li>Take <span className="font-semibold text-emerald-300">3 slow breaths</span>.</li>
                  <li>Use this flow to speak. Then let them have a turn.</li>
                </ol>
              </div>
            )}

            {currentStep === 1 && (
              <div className={`rounded-2xl bg-gradient-to-br ${steps[1].color} ${steps[1].ring} ring-1 p-5`}>
                <h2 className="text-lg font-semibold mb-1">{steps[1].title}</h2>
                <p className="text-xs text-slate-200 mb-3">{steps[1].subtitle}</p>
                <Textarea value={state.blue} onChange={(e) => setState({...state, blue: e.target.value})} className="bg-slate-900/60 border-slate-700/70 text-white min-h-[90px]" placeholder='Example: "You walked out while I was talking."' />
              </div>
            )}

            {currentStep === 2 && (
              <div className={`rounded-2xl bg-gradient-to-br ${steps[2].color} ${steps[2].ring} ring-1 p-5`}>
                <h2 className="text-lg font-semibold mb-1">{steps[2].title}</h2>
                <p className="text-xs text-slate-200 mb-3">{steps[2].subtitle}</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {feelings.map(f => (
                    <button key={f} onClick={() => addFeeling(f)} className="px-2.5 py-1 rounded-full border border-red-400/60 text-xs text-red-50/90 hover:bg-red-500/70 hover:text-red-950 transition">
                      {f}
                    </button>
                  ))}
                </div>
                <Textarea value={state.red} onChange={(e) => setState({...state, red: e.target.value})} className="bg-slate-900/60 border-slate-700/70 text-white min-h-[70px]" placeholder='Example: "I feel hurt and overwhelmed."' />
              </div>
            )}

            {currentStep === 3 && (
              <div className={`rounded-2xl bg-gradient-to-br ${steps[3].color} ${steps[3].ring} ring-1 p-5`}>
                <h2 className="text-lg font-semibold mb-1">{steps[3].title}</h2>
                <p className="text-xs text-slate-200 mb-3">{steps[3].subtitle}</p>
                <Textarea value={state.green} onChange={(e) => setState({...state, green: e.target.value})} className="bg-slate-900/60 border-slate-700/70 text-white min-h-[80px]" placeholder='Example: "I need to feel heard and respected."' />
              </div>
            )}

            {currentStep === 4 && (
              <div className={`rounded-2xl bg-gradient-to-br ${steps[4].color} ${steps[4].ring} ring-1 p-5`}>
                <h2 className="text-lg font-semibold mb-1">{steps[4].title}</h2>
                <p className="text-xs text-slate-200 mb-3">{steps[4].subtitle}</p>
                <Textarea value={state.gold} onChange={(e) => setState({...state, gold: e.target.value})} className="bg-slate-900/60 border-slate-700/70 text-white min-h-[80px]" placeholder='Example: "Take a 10-minute break and talk again?"' />
              </div>
            )}

            {currentStep === 5 && (
              <div className={`rounded-2xl bg-gradient-to-br ${steps[5].color} ring-1 ring-emerald-500/60 p-5`}>
                <h2 className="text-lg font-semibold mb-1">{steps[5].title}</h2>
                <p className="text-xs text-slate-200 mb-3">{steps[5].subtitle}</p>
                <Textarea value={buildGoldSentence()} className="bg-slate-900/60 border-slate-700/70 text-white min-h-[110px]" readOnly />
              </div>
            )}
          </div>

          <div className="mt-5 flex justify-between gap-3">
            <Button onClick={() => currentStep > 0 && setCurrentStep(currentStep - 1)} disabled={currentStep === 0} variant="outline" className="w-1/3 border-slate-600 text-slate-200">
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            <Button onClick={() => {
              if (currentStep < 5) setCurrentStep(currentStep + 1);
              else { setCurrentStep(0); setState({ blue: '', red: '', green: '', gold: '' }); }
            }} className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-emerald-950">
              {currentStep === 5 ? 'Done' : 'Next'} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
